import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'
import SendIcon from './assets/send-paper-rocket-svg'
import ArrowUpIcon from './assets/arrow-up'
import type { OpenedSurvey } from './types'
import { cn } from './cn'
import { useOffsetContainerCtx } from './OffsetContainer'

const defaultInputHeight = (fullscreen: boolean) => (fullscreen ? 48 : 40)
const maxInputHeight = 112

const useAutosizeTextArea = (
  textAreaRef: HTMLElement | null,
  value: string
) => {
  const isTouched = useOffsetContainerCtx(s => s.isTouched)
  const device = useOffsetContainerCtx(s => s.device)

  const isFullscreen = device === 'default' && isTouched

  useEffect(() => {
    if (textAreaRef && textAreaRef.tagName === 'TEXTAREA') {
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      textAreaRef.style.height = '0px'
      const scrollHeight = Math.max(
        Math.min(textAreaRef.scrollHeight, maxInputHeight),
        defaultInputHeight(isFullscreen)
      )

      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will product an incorrect value.
      textAreaRef.style.height = scrollHeight + 'px'
    }
  }, [value, isFullscreen])
}

export type InputProps = {
  questionId: string
  onSubmit: (values: {
    text: string
    questionId: string
    choiceId?: string
  }) => void
  loadingResponse: boolean
  role: OpenedSurvey['question']['role']
  onFocus: () => void
  onBlur: () => void
  hasMounted: React.MutableRefObject<boolean>
  autoFocus?: boolean
}

export type InputAPI = {
  focus: () => void
}

const SubmitButton = ({
  onClick,
  value,
}: {
  onClick: () => void
  value: string
}) => {
  const isTouched = useOffsetContainerCtx(s => s.isTouched)
  const device = useOffsetContainerCtx(s => s.device)

  const isFullscreen = device === 'default' && isTouched

  if (isFullscreen) {
    return (
      <button
        type="submit"
        className={cn(
          'sp-absolute sp-right-2 sp-transition-colors sp-bg-survey-send-button-background sp-text-survey-send-button-content sp-rounded-[50%] disabled:sp-bg-opacity-20',
          {
            'sp-bottom-2': isFullscreen,
            'sp-bottom-1': !isFullscreen,
          }
        )}
        disabled={!value}
        onMouseDown={e => {
          e.preventDefault()
          e.stopPropagation()
          onClick()
        }}>
        <ArrowUpIcon width={32} />
      </button>
    )
  }

  return (
    <button
      type="submit"
      className="sp-absolute sp-right-[15px] sp-bottom-[8px] sp-text-survey-icon hover:sp-text-survey-text-user sp-transition-colors"
      onMouseDown={e => {
        e.preventDefault()
        e.stopPropagation()

        onClick()
      }}>
      <SendIcon width={24} />
    </button>
  )
}

const Input = forwardRef<InputAPI, InputProps>(
  (
    {
      onSubmit,
      questionId,
      loadingResponse,
      onFocus,
      onBlur,
      hasMounted,
      autoFocus = false,
    },
    ref
  ) => {
    const [value, setValue] = useState('')
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const formRef = useRef<HTMLFormElement>(null)

    useAutosizeTextArea(inputRef.current, value)

    useImperativeHandle(
      ref,
      (): InputAPI => ({
        focus: () => {
          inputRef.current?.focus()
        },
      })
    )

    const isTouched = useOffsetContainerCtx(s => s.isTouched)
    const device = useOffsetContainerCtx(s => s.device)

    const isFullscreen = device === 'default' && isTouched

    const handleChange = (
      e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      setValue(e.target?.value || '')
    }

    const handleSubmit = ({
      choiceId,
      text,
    }: {
      choiceId?: string
      text: string
    }) => {
      if (!text) {
        return
      }

      if (!loadingResponse) {
        onSubmit({
          choiceId,
          text,
          questionId,
        })
        setValue('')
      }
    }

    useEffect(() => {
      if (hasMounted.current) {
        formRef.current?.scrollIntoView()
      }

      if (hasMounted.current) {
        inputRef.current?.focus()
      } else {
        hasMounted.current = true
      }
    }, [loadingResponse])

    const onKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()

        handleSubmit({ text: value })
      }
    }

    return (
      <div
        className={cn('sp-mt-5', {
          'sp-px-5': !isFullscreen,
          'sp-px-3': isFullscreen,
        })}>
        <form
          onSubmit={e => {
            e.preventDefault()
            e.stopPropagation()

            handleSubmit({ text: value })
          }}
          ref={formRef}
          className={cn('sp-relative sp-leading-[0]')}
          noValidate>
          <textarea
            ref={inputRef}
            className={cn(
              'sp-bg-survey-input sp-placeholder-survey-text-placeholder sp-pr-[47px] sp-w-full sp-resize-none focus:sp-outline-none sp-text-input',
              {
                'sp-py-2 sp-pl-4 sp-rounded-[20px]': !isFullscreen,
                'sp-py-3 sp-pl-5 sp-rounded-[70px]': isFullscreen,
              }
            )}
            style={{
              minHeight: defaultInputHeight(isFullscreen),
            }}
            autoComplete="off"
            placeholder="Mesage..."
            onChange={handleChange}
            onKeyDown={onKeyPress}
            autoFocus={autoFocus}
            onFocus={onFocus}
            onBlur={onBlur}
            rows={1}
            value={value}
          />
          <SubmitButton
            value={value}
            onClick={() => handleSubmit({ text: value })}
          />
        </form>
      </div>
    )
  }
)

export default Input
